import {useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {FELT, FeltDefs, useFeltIds} from './felt';
import {
  Beanie,
  CatFace,
  OllieHead,
  PlushPiMark,
  type Mood,
} from './feltFriends';
import {Hop, ToyShell, type MapToyProps} from './mapToys';
import {playTones, type Tone} from './toySounds';
import {
  DOODLE_FEATURE_NAMES,
  DOODLES,
  doodleProbability,
  formatProbability,
  nextDoodle,
  SIGMOID_SLIDE,
  slideBedY,
  slideStops,
  sortNextItem,
  type DoodleFeature,
  type DoodleSubject,
  type SortedItem,
  type SortingKind,
} from './week4Machines';

/**
 * Week 4 map toys, for logistic regression: a playground slide shaped like
 * the sigmoid, Lesson 8's sorting line asking Section 4's "Is it a
 * sneaker?", and a doodle machine whose chance of "cat" jumps by each
 * feature's weight as the doodle is drawn. Their stories live in
 * `week4Machines.ts`. Like the other map toys they carry no words;
 * {@link ToyShell} names them and announces each change.
 */

/** The lectures' probability pink, from Lesson 8's machine. */
const PROBABILITY_PINK = '#FF7FFC';
const PROBABILITY_PINK_DARK = '#B03CB0';
const WOOD = '#B07A4A';
const WOOD_DARK = '#8A5A34';
const PENCIL = '#5B5552';

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const appear = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/** Fades in after `$delay` seconds; shown at once with reduced motion. */
const Appear = styled.g<{$delay: number}>`
  animation: ${appear} 0.25s ease-out ${p => p.$delay}s both;
  ${reducedMotion}
`;

/** A pink probability pill centred at (x, y), as Lesson 8 draws them. */
function ProbabilityPill({x, y, text}: {x: number; y: number; text: string}) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect
        x={-14}
        y={-7}
        width={28}
        height={14}
        rx={7}
        fill={PROBABILITY_PINK}
        stroke={PROBABILITY_PINK_DARK}
        strokeWidth={0.9}
      />
      <text
        x={0}
        y={3.4}
        textAnchor="middle"
        fontSize={9.6}
        fontWeight={700}
        fill={FELT.ink}
      >
        {text}
      </text>
    </g>
  );
}

// The sigmoid slide.

const RIDE_SECONDS = 1.5;
const TRAIN_GAP_SECONDS = 0.3;
const TRAIN_CHANCE = 1 / 8;

const RIDERS = [
  {id: 'beanie', name: 'Beanie'},
  {id: 'ollie', name: 'Ollie'},
  {id: 'cat', name: 'the toolbox cat'},
  {id: 'pi', name: 'Plush π'},
] as const;

type RiderId = (typeof RIDERS)[number]['id'];

/** A friend about 12 units across, centred at the origin. */
function Rider({id, mood}: {id: RiderId; mood: Mood}) {
  switch (id) {
    case 'beanie':
      return (
        <g transform="scale(0.95)">
          <Beanie mood={mood} />
        </g>
      );
    case 'ollie':
      return (
        <g transform="scale(0.7)">
          <OllieHead mood={mood} />
        </g>
      );
    case 'cat':
      return (
        <g transform="scale(0.68)">
          <CatFace mood={mood} />
        </g>
      );
    case 'pi':
      return (
        <g transform="scale(0.72)">
          <PlushPiMark mood={mood} />
        </g>
      );
  }
}

const RIDE_STOPS = slideStops();
const RIDE_END = RIDE_STOPS[RIDE_STOPS.length - 1] ?? {x: 0, y: 0, angle: 0};

const ride = keyframes`${RIDE_STOPS.map(
  stop =>
    `${stop.percent.toFixed(2)}% { transform: translate(${stop.x.toFixed(2)}px, ${stop.y.toFixed(2)}px) rotate(${stop.angle.toFixed(1)}deg); }`,
).join('\n')}`;

/**
 * Rides the slide from top to bottom after `$delay` seconds and stays at
 * the bottom; with reduced motion it is simply at the bottom.
 */
const Riding = styled.g<{$delay: number}>`
  transform: translate(${RIDE_END.x}px, ${RIDE_END.y}px)
    rotate(${RIDE_END.angle}deg);
  animation: ${ride} ${RIDE_SECONDS}s linear ${p => p.$delay}s both;
  ${reducedMotion}
`;

/** Where the next rider waits, on the platform at the top. */
const WAIT = {
  x: SIGMOID_SLIDE.highX + 7,
  y: slideBedY(SIGMOID_SLIDE.highX) - SIGMOID_SLIDE.seat,
};

/** The bed's top edge, from the low end to the high end. */
const BED_TOP = Array.from(
  {length: SIGMOID_SLIDE.highX - SIGMOID_SLIDE.lowX + 1},
  (_, index) => {
    const x = SIGMOID_SLIDE.lowX + index;
    return {x, y: slideBedY(x)};
  },
);

const BED_PATH = [
  `M ${BED_TOP.map(point => `${point.x} ${point.y.toFixed(2)}`).join(' L ')}`,
  `L ${[...BED_TOP]
    .reverse()
    .map(point => `${point.x} ${(point.y + 4).toFixed(2)}`)
    .join(' L ')} Z`,
].join(' ');

const RAIL_PATH = `M ${BED_TOP.map(
  point => `${point.x} ${(point.y - 2.4).toFixed(2)}`,
).join(' L ')}`;

const WHEE: readonly Tone[] = [
  1318.5, 1174.7, 1046.5, 880, 784, 659.3, 587.3, 523.3,
].map((frequency, index) => ({
  frequency,
  startSec: 0.1 + index * 0.1,
  durationSec: 0.14,
}));

const LANDING: readonly Tone[] = [
  {frequency: 392, startSec: RIDE_SECONDS - 0.15, durationSec: 0.25},
];

interface SlideState {
  /** How many turns have been taken. */
  turns: number;
  /** Whether the last turn was all four friends at once. */
  isTrain: boolean;
}

function slideCaption(state: SlideState): string {
  if (state.turns === 0) {
    return 'take a turn';
  }
  if (state.isTrain) {
    return 'everyone slid down the sigmoid together';
  }
  const rider = RIDERS[(state.turns - 1) % RIDERS.length];
  return `${rider?.name ?? 'Beanie'} slid down the sigmoid, fastest where it is steepest`;
}

/**
 * A playground slide shaped like the sigmoid: each press, the next friend
 * pushes off the flat top, speeds through the steep middle and coasts on
 * the flat bottom; now and then everyone goes at once.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToySigmoidSlide({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [state, setState] = useState<SlideState>({turns: 0, isTrain: false});

  const press = () => {
    const isTrain = state.turns > 0 && Math.random() < TRAIN_CHANCE;
    setState({turns: state.turns + 1, isTrain});
    const count = isTrain ? RIDERS.length : 1;
    for (let rider = 0; rider < count; rider += 1) {
      const offset = rider * TRAIN_GAP_SECONDS;
      playTones({
        tones: [...WHEE, ...LANDING].map(tone => ({
          ...tone,
          startSec: tone.startSec + offset,
        })),
      });
    }
  };

  const riders: {id: RiderId; delay: number}[] =
    state.turns === 0
      ? []
      : state.isTrain
        ? RIDERS.map((rider, index) => ({
            id: rider.id,
            delay: index * TRAIN_GAP_SECONDS,
          }))
        : [
            {
              id: RIDERS[(state.turns - 1) % RIDERS.length]?.id ?? 'beanie',
              delay: 0,
            },
          ];
  const waiting = RIDERS[state.turns % RIDERS.length]?.id ?? 'beanie';
  const lastDelay = riders.reduce(
    (latest, rider) => Math.max(latest, rider.delay),
    0,
  );
  const {groundY, lowX, highX} = SIGMOID_SLIDE;
  const platformY = slideBedY(highX);

  return (
    <ToyShell
      isPreview={isPreview}
      label="Sigmoid slide"
      caption={slideCaption(state)}
      onPoke={press}
    >
      <svg
        viewBox="0 0 130 92"
        width={size}
        height={(size * 92) / 130}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <ellipse
          cx={64}
          cy={groundY + 2}
          rx={60}
          ry={3.5}
          fill="rgba(0,0,0,0.08)"
        />
        <rect
          x={lowX - 8}
          y={groundY - 3}
          width={24}
          height={5}
          rx={2.5}
          fill={FELT.mint}
          filter={`url(#${ids.fuzz})`}
        />
        {[32, 57, 82].map(x => (
          <path
            key={x}
            d={`M ${x} ${slideBedY(x) + 3} V ${groundY}`}
            stroke={WOOD}
            strokeWidth={2.6}
            strokeLinecap="round"
          />
        ))}
        <rect
          x={highX - 2}
          y={platformY}
          width={16}
          height={4}
          rx={1.5}
          fill={WOOD}
        />
        {[highX + 4, highX + 13].map(x => (
          <path
            key={x}
            d={`M ${x} ${platformY - 8} V ${groundY}`}
            stroke={WOOD_DARK}
            strokeWidth={2}
            strokeLinecap="round"
          />
        ))}
        {[24, 34, 44, 54, 64, 74].map(y => (
          <path
            key={y}
            d={`M ${highX + 4} ${y} H ${highX + 13}`}
            stroke={WOOD_DARK}
            strokeWidth={1.6}
          />
        ))}
        <path
          d={`M ${highX + 4} ${platformY - 8} H ${highX + 13}`}
          stroke={FELT.cardinal}
          strokeWidth={1.8}
          strokeLinecap="round"
        />
        <path
          d={BED_PATH}
          fill={FELT.pink}
          stroke="#D97E99"
          strokeWidth={0.8}
          filter={`url(#${ids.fuzz})`}
        />
        <path
          d={RAIL_PATH}
          fill="none"
          stroke={FELT.cardinal}
          strokeWidth={1.8}
          strokeLinecap="round"
        />
        <Appear
          key={`wait-${state.turns}`}
          $delay={state.turns === 0 ? 0 : lastDelay + RIDE_SECONDS}
        >
          <g transform={`translate(${WAIT.x} ${WAIT.y})`}>
            <Rider id={waiting} mood="calm" />
          </g>
        </Appear>
        {riders.map(rider => (
          <Riding key={`${state.turns}-${rider.id}`} $delay={rider.delay}>
            <Rider id={rider.id} mood="happy" />
          </Riding>
        ))}
      </svg>
    </ToyShell>
  );
}

// The sorting line.

const SORT_SECONDS = 2.2;
/** When the machine shows its call, in seconds into the ride. */
const CALL_SECONDS = 1.05;
const BELT_Y = 50;
const MACHINE_X = 60;
const GATE_X = 102;
const OTHER_BIN_X = 138;
const BIN_FLOOR_Y = 85;
const BIN_SLOTS = [-6, 0, 6];

/** Where an item lands: its bin's x plus a slot, on the bin floor. */
function binSpot(item: {isCalledSneaker: boolean; slot: number}) {
  return {
    x:
      (item.isCalledSneaker ? GATE_X : OTHER_BIN_X) +
      (BIN_SLOTS[item.slot % BIN_SLOTS.length] ?? 0),
    y: BIN_FLOOR_Y,
  };
}

function sortRide({
  x,
  y,
  isCalledSneaker,
}: {
  x: number;
  y: number;
  isCalledSneaker: boolean;
}) {
  const start = 10;
  const at = (dx: number, dy: number) =>
    `transform: translate(${dx}px, ${dy}px);`;
  const steps = isCalledSneaker
    ? [
        `0% { ${at(start, BELT_Y)} }`,
        `30% { ${at(MACHINE_X, BELT_Y)} }`,
        `48% { ${at(MACHINE_X, BELT_Y)} }`,
        `68% { ${at(GATE_X, BELT_Y)} }`,
        `86% { ${at(x, y)} animation-timing-function: ease-out; }`,
        `100% { ${at(x, y)} }`,
      ]
    : [
        `0% { ${at(start, BELT_Y)} }`,
        `30% { ${at(MACHINE_X, BELT_Y)} }`,
        `48% { ${at(MACHINE_X, BELT_Y)} }`,
        `66% { ${at(GATE_X, BELT_Y)} }`,
        `84% { ${at(OTHER_BIN_X, BELT_Y)} }`,
        `100% { ${at(x, y)} }`,
      ];
  return keyframes`${steps.join('\n')}`;
}

/** Rides the line into a bin; with reduced motion it is already in the bin. */
const Sorting = styled.g<{$x: number; $y: number; $isCalledSneaker: boolean}>`
  transform: translate(${p => p.$x}px, ${p => p.$y}px);
  animation: ${p =>
      sortRide({x: p.$x, y: p.$y, isCalledSneaker: p.$isCalledSneaker})}
    ${SORT_SECONDS}s ease-in-out both;
  ${reducedMotion}
`;

const trapdoor = keyframes`
  0%, 62% { transform: rotate(0deg); }
  70%, 88% { transform: rotate(70deg); }
  100% { transform: rotate(0deg); }
`;

/** The gate's flap, which drops open for a sneaker. */
const Trapdoor = styled.g<{$isOpening: boolean}>`
  transform-origin: ${GATE_X - 8}px ${BELT_Y}px;
  ${p =>
    p.$isOpening
      ? css`
          animation: ${trapdoor} ${SORT_SECONDS}s linear both;
        `
      : ''}
  ${reducedMotion}
`;

const blink = keyframes`
  0%, 100% { fill: ${FELT.gold}; }
  50% { fill: #FFF3C4; }
`;

/** The machine's light, which blinks while it reads the item. */
const ThinkingLight = styled.circle<{$isThinking: boolean}>`
  ${p =>
    p.$isThinking
      ? css`
          animation: ${blink} 0.18s linear 0.66s 3;
        `
      : ''}
  ${reducedMotion}
`;

/** A Fashion-MNIST-style item about 16 units wide, its base at the origin. */
function SortingPicture({kind}: {kind: SortingKind}) {
  switch (kind) {
    case 'sneaker':
      return (
        <g>
          <path
            d="M -8 -2 V -6 Q -7.5 -9.5 -3 -9.5 L 0 -6.5 Q 5 -6 8 -3.5 V -2 Z"
            fill="#FFFFFF"
            stroke="#A8A29E"
            strokeWidth={0.7}
          />
          <rect
            x={-8.6}
            y={-2.4}
            width={17.2}
            height={2.4}
            rx={1.2}
            fill="#D6D3D1"
          />
          <path
            d="M -3 -7.6 L -1 -6.4 M -1.6 -8.6 L 0.4 -7.2 M -5 -8 L -6 -5"
            stroke={FELT.pink}
            strokeWidth={0.9}
            strokeLinecap="round"
          />
        </g>
      );
    case 'sandal':
      return (
        <g>
          <rect x={-8.6} y={-2} width={17.2} height={2} rx={1} fill="#C2A27A" />
          <path
            d="M -5 -2 Q -4 -7 -1 -7 Q 1 -7 2 -2 M 3 -2 Q 5 -5.5 7 -2"
            fill="none"
            stroke="#8A5A34"
            strokeWidth={1.4}
            strokeLinecap="round"
          />
        </g>
      );
    case 'boot':
      return (
        <path
          d="M -7.5 0 V -13 H -1 V -6 Q 5 -6 8 -2.5 V 0 Z"
          fill="#9A6B45"
          stroke="#6E4A2F"
          strokeWidth={0.7}
        />
      );
    case 'tee':
      return (
        <path
          d="M -3 -12 L -1.6 -11.4 Q 0 -10 1.6 -11.4 L 3 -12 L 8 -9 L 6.4 -6 L 4.4 -7 V 0 H -4.4 V -7 L -6.4 -6 L -8 -9 Z"
          fill={FELT.blue}
          stroke="#3B6CA3"
          strokeWidth={0.6}
        />
      );
    case 'bag':
      return (
        <g>
          <path
            d="M -3.6 -8 Q -3.6 -13 0 -13 Q 3.6 -13 3.6 -8"
            fill="none"
            stroke="#A87420"
            strokeWidth={1.2}
          />
          <rect x={-7} y={-9} width={14} height={9} rx={2} fill={FELT.gold} />
        </g>
      );
    case 'ollie':
      return (
        <g transform="translate(0 -7) scale(0.62)">
          <OllieHead mood="happy" />
        </g>
      );
  }
}

/** A bin's back, drawn behind its items. */
function BinBack({x}: {x: number}) {
  return (
    <rect x={x - 11} y={68} width={22} height={24} rx={3} fill="#CDB89A" />
  );
}

/** A bin's front, drawn over its items, with the bin's mark. */
function BinFront({
  x,
  isSneakerBin,
  feltId,
}: {
  x: number;
  isSneakerBin: boolean;
  feltId: string;
}) {
  return (
    <g>
      <rect
        x={x - 12}
        y={80}
        width={24}
        height={12}
        rx={3}
        fill={isSneakerBin ? '#E7D2B4' : '#DDD3C4'}
        stroke={isSneakerBin ? PROBABILITY_PINK_DARK : '#A8A29E'}
        strokeWidth={1}
        filter={`url(#${feltId})`}
      />
      <g transform={`translate(${x} 89.5) scale(0.5)`}>
        <SortingPicture kind="sneaker" />
      </g>
      {!isSneakerBin && (
        <path
          d={`M ${x - 4.5} 83 L ${x + 4.5} 90`}
          stroke="#78716C"
          strokeWidth={1.3}
          strokeLinecap="round"
        />
      )}
    </g>
  );
}

/** An item on the line or in a bin. */
interface PlacedItem extends SortedItem {
  /** Counts every item sent, so each has its own key. */
  id: number;
  /** Which of its bin's spots it lands in. */
  slot: number;
}

interface SortingState {
  /** The latest items, oldest first; the last is riding now. */
  items: PlacedItem[];
  /** How many items have gone into each bin. */
  sneakerCount: number;
  otherCount: number;
}

function sortingCaption(state: SortingState): string {
  const item = state.items[state.items.length - 1];
  if (!item) {
    return 'send something down the line';
  }
  const probability = formatProbability(item.probability);
  const bin = item.isCalledSneaker ? 'the sneaker bin' : 'the other bin';
  if (item.kind === 'ollie') {
    return `Ollie rode the line: ${probability} sneaker, into ${bin}`;
  }
  const wrong = item.isRight ? '' : ', wrong this time';
  return `${item.name}: ${probability} sneaker, into ${bin}${wrong}`;
}

const BELT_TICKS: readonly Tone[] = [0, 0.18, 0.36, 0.54].map(startSec => ({
  frequency: 1567.98,
  startSec,
  durationSec: 0.03,
}));

/**
 * Lesson 8's sorting line on Section 4's question, "Is it a sneaker?":
 * each press sends an item through the machine, which shows its
 * probability in pink; the gate drops it in the sneaker bin when the
 * probability is at least 0.5, and sandals often fool it.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToySortingLine({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [state, setState] = useState<SortingState>({
    items: [],
    sneakerCount: 0,
    otherCount: 0,
  });

  const press = () => {
    const item = sortNextItem({random: Math.random});
    const {sneakerCount, otherCount} = state;
    const placed: PlacedItem = {
      ...item,
      id: sneakerCount + otherCount,
      slot: item.isCalledSneaker ? sneakerCount : otherCount,
    };
    setState({
      items: [...state.items.slice(-8), placed],
      sneakerCount: sneakerCount + (item.isCalledSneaker ? 1 : 0),
      otherCount: otherCount + (item.isCalledSneaker ? 0 : 1),
    });
    playTones({
      tones: [
        ...BELT_TICKS,
        {
          frequency: item.isCalledSneaker ? 1318.5 : 659.3,
          startSec: CALL_SECONDS,
          durationSec: 0.35,
        },
      ],
      wave: 'triangle',
    });
  };

  const current = state.items[state.items.length - 1];
  const settled = state.items.slice(0, -1);
  const shownInBin = (isCalledSneaker: boolean) =>
    settled
      .filter(item => item.isCalledSneaker === isCalledSneaker)
      .slice(-BIN_SLOTS.length + 1);
  const currentSpot = current ? binSpot(current) : undefined;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Sorting line"
      caption={sortingCaption(state)}
      onPoke={press}
    >
      <svg
        viewBox="0 0 150 96"
        width={size}
        height={(size * 96) / 150}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <ellipse cx={76} cy={94} rx={70} ry={3} fill="rgba(0,0,0,0.08)" />
        {[10, 84].map(x => (
          <path
            key={x}
            d={`M ${x} ${BELT_Y + 6} V 92`}
            stroke="#78716C"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        ))}
        <BinBack x={GATE_X} />
        <BinBack x={OTHER_BIN_X} />
        {[true, false].flatMap(isCalledSneaker =>
          shownInBin(isCalledSneaker).map(item => {
            const spot = binSpot(item);
            return (
              <g key={item.id} transform={`translate(${spot.x} ${spot.y})`}>
                <SortingPicture kind={item.kind} />
              </g>
            );
          }),
        )}
        {current && currentSpot && (
          <Sorting
            key={current.id}
            $x={currentSpot.x}
            $y={currentSpot.y}
            $isCalledSneaker={current.isCalledSneaker}
          >
            <SortingPicture kind={current.kind} />
          </Sorting>
        )}
        <rect
          x={4}
          y={BELT_Y}
          width={GATE_X - 12}
          height={6}
          rx={3}
          fill="#57534E"
        />
        <rect
          x={GATE_X + 8}
          y={BELT_Y}
          width={OTHER_BIN_X - GATE_X - 12}
          height={6}
          rx={3}
          fill="#57534E"
        />
        {[10, 22, 34, 46, 74, 86, 116, 128].map(x => (
          <circle key={x} cx={x} cy={BELT_Y + 3} r={2} fill="#A8A29E" />
        ))}
        <Trapdoor
          key={`gate-${current?.id ?? -1}`}
          $isOpening={current?.isCalledSneaker ?? false}
        >
          <rect
            x={GATE_X - 8}
            y={BELT_Y}
            width={16}
            height={3}
            rx={1.5}
            fill={PROBABILITY_PINK_DARK}
          />
        </Trapdoor>
        <BinFront x={GATE_X} isSneakerBin feltId={ids.fuzz} />
        <BinFront x={OTHER_BIN_X} isSneakerBin={false} feltId={ids.fuzz} />
        <rect
          x={MACHINE_X - 20}
          y={20}
          width={40}
          height={BELT_Y - 18}
          rx={6}
          fill={FELT.cardinal}
          filter={`url(#${ids.fuzz})`}
        />
        <rect
          x={MACHINE_X - 13}
          y={BELT_Y - 16}
          width={26}
          height={18}
          rx={4}
          fill="#3F1512"
        />
        <rect
          x={MACHINE_X - 14}
          y={25}
          width={28}
          height={6}
          rx={3}
          fill={FELT.creamLight}
        />
        <text
          x={MACHINE_X}
          y={30}
          textAnchor="middle"
          fontSize={5.6}
          fontWeight={700}
          fill={FELT.cardinal}
        >
          σ
        </text>
        <ThinkingLight
          key={`light-${current?.id ?? -1}`}
          cx={MACHINE_X + 15}
          cy={17}
          r={2.6}
          fill={FELT.gold}
          $isThinking={Boolean(current)}
        />
        <path
          d={`M ${MACHINE_X + 15} 19.6 V 20`}
          stroke={FELT.ink}
          strokeWidth={1}
        />
        {current ? (
          <Appear key={`call-${current.id}`} $delay={CALL_SECONDS}>
            <Hop>
              <ProbabilityPill
                x={MACHINE_X}
                y={11}
                text={formatProbability(current.probability)}
              />
            </Hop>
          </Appear>
        ) : (
          <ProbabilityPill x={MACHINE_X} y={11} text="?" />
        )}
      </svg>
    </ToyShell>
  );
}

// The doodle machine.

const DOODLE_CENTER = {x: 36, y: 52};
const METER = {x: 88, top: 34, bottom: 82, width: 8};

/** Draws its path on, like a pencil, when it mounts. */
const PencilStroke = styled.path`
  stroke-dasharray: 1;
  animation: ${keyframes`
    from { stroke-dashoffset: 1; }
    to { stroke-dashoffset: 0; }
  `} 0.55s ease-in-out both;
  ${reducedMotion}
`;

/** The meter's pink fill, which rises or falls to the new probability. */
const MeterFill = styled.rect`
  transform-box: fill-box;
  transform-origin: bottom;
  transition: transform 0.5s cubic-bezier(0.34, 1.3, 0.64, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** Each feature's pencil path, around a head of radius 13 at the origin. */
const FEATURE_PATHS: Readonly<Record<DoodleFeature, string>> = {
  pointyEars:
    'M -11.5 -6 L -10 -19 L -3.5 -12.6 M 11.5 -6 L 10 -19 L 3.5 -12.6',
  roundEars:
    'M -5.4 -11.6 A 4.6 4.6 0 1 1 -12.4 -6.6 M 5.4 -11.6 A 4.6 4.6 0 1 0 12.4 -6.6',
  eyes: 'M -4.6 -3.4 V -1.4 M 4.6 -3.4 V -1.4',
  whiskers:
    'M -1.4 2 H 1.4 L 0 3.6 Z M -4 4 L -15 1.4 M -4 6 L -15 7 M 4 4 L 15 1.4 M 4 6 L 15 7',
  mane:
    Array.from({length: 18}, (_, index) => {
      const angle = (index / 18) * 2 * Math.PI - Math.PI / 2;
      const radius = index % 2 === 0 ? 20 : 16;
      const x = (radius * Math.cos(angle)).toFixed(2);
      const y = (radius * Math.sin(angle)).toFixed(2);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ') + ' Z',
  snout: 'M -6 5 A 6 4.4 0 1 0 6 5 A 6 4.4 0 1 0 -6 5 M -1.6 2.6 H 1.6',
};

interface DoodleState {
  subject: DoodleSubject;
  /** How many of its features are drawn. */
  drawn: number;
  /** How many pages have been started, counting this one. */
  pages: number;
  /** Whether the toy has been pressed. */
  hasStarted: boolean;
}

function doodleCaption(state: DoodleState): string {
  if (!state.hasStarted) {
    return 'draw something';
  }
  const doodle = DOODLES[state.subject];
  const probability = formatProbability(
    doodleProbability({doodle, drawn: state.drawn}),
  );
  if (state.drawn === 0) {
    return `a new doodle, just a head: cat ${probability}`;
  }
  const feature = doodle.features[state.drawn - 1] ?? 'eyes';
  const name = DOODLE_FEATURE_NAMES[feature];
  const note =
    feature === 'eyes' ? ', no change, since every doodle has eyes' : '';
  const isDone = state.drawn === doodle.features.length;
  if (!isDone) {
    return `${name}: cat ${probability}${note}`;
  }
  const reveal =
    state.subject === 'ollie'
      ? "It's Ollie! The whiskers fooled it."
      : `It was ${doodle.name}.`;
  return `${name}: cat ${probability}${note}. ${reveal}`;
}

const SCRIBBLE: readonly Tone[] = [0, 0.07, 0.15, 0.24].map(startSec => ({
  frequency: 2093 + startSec * 900,
  startSec,
  durationSec: 0.025,
}));

const DOODLE_DONE: readonly Tone[] = [
  {frequency: 784, startSec: 0.5, durationSec: 0.2},
  {frequency: 1046.5, startSec: 0.62, durationSec: 0.35},
];

/**
 * Section 4's "Is it a cat?" as a sketchpad: each press draws one more
 * feature of a doodle, and the pink meter beside it jumps by that
 * feature's weight, σ(θ₀ + the weights drawn so far). Eyes move nothing,
 * since every doodle has them; whiskers can fool it into calling Ollie a
 * cat. When a doodle is done, the next press turns the page.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyDoodleMachine({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [state, setState] = useState<DoodleState>({
    subject: 'cat',
    drawn: 0,
    pages: 1,
    hasStarted: false,
  });
  const doodle = DOODLES[state.subject];
  const isDone = state.drawn === doodle.features.length;
  const probability = doodleProbability({doodle, drawn: state.drawn});

  const press = () => {
    if (!isDone) {
      setState({...state, drawn: state.drawn + 1, hasStarted: true});
      playTones({
        tones:
          state.drawn + 1 === doodle.features.length
            ? [...SCRIBBLE, ...DOODLE_DONE]
            : SCRIBBLE,
        wave: 'triangle',
      });
      return;
    }
    setState({
      subject: nextDoodle({random: Math.random, previous: state.subject}),
      drawn: 0,
      pages: state.pages + 1,
      hasStarted: true,
    });
    playTones({tones: SCRIBBLE, wave: 'triangle'});
  };

  const meterHeight = METER.bottom - METER.top;
  const halfY = METER.bottom - meterHeight / 2;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Doodle machine"
      caption={doodleCaption(state)}
      onPoke={press}
    >
      <svg
        viewBox="0 0 110 90"
        width={size}
        height={(size * 90) / 110}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <ellipse cx={55} cy={88} rx={48} ry={3} fill="rgba(0,0,0,0.08)" />
        <rect
          x={6}
          y={14}
          width={60}
          height={72}
          rx={4}
          fill={FELT.creamLight}
          stroke={FELT.creamDark}
          strokeWidth={1.6}
          filter={`url(#${ids.fuzz})`}
        />
        {[14, 24, 34, 44, 54].map(x => (
          <rect
            key={x}
            x={x}
            y={10}
            width={3}
            height={8}
            rx={1.5}
            fill="#A8A29E"
          />
        ))}
        <g
          key={`page-${state.pages}`}
          transform={`translate(${DOODLE_CENTER.x} ${DOODLE_CENTER.y})`}
          fill="none"
          stroke={PENCIL}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <PencilStroke
            d="M -13 0 A 13 13 0 1 0 13 0 A 13 13 0 1 0 -13 0"
            pathLength={1}
          />
          {doodle.features.slice(0, state.drawn).map(feature => (
            <PencilStroke
              key={feature}
              d={FEATURE_PATHS[feature]}
              pathLength={1}
              strokeWidth={feature === 'eyes' ? 2.6 : 1.5}
            />
          ))}
        </g>
        {isDone && state.subject === 'ollie' && (
          <Appear key={`ollie-${state.pages}`} $delay={0.5}>
            <Hop>
              <g transform="translate(60 78) scale(0.8)">
                <OllieHead mood="happy" />
              </g>
            </Hop>
          </Appear>
        )}
        <g transform={`translate(${METER.x} 18) scale(0.62)`}>
          <CatFace mood={probability >= 0.5 ? 'happy' : 'calm'} />
        </g>
        <rect
          x={METER.x - METER.width / 2}
          y={METER.top}
          width={METER.width}
          height={meterHeight}
          rx={METER.width / 2}
          fill="#FFFFFF"
          stroke="#D6D3D1"
          strokeWidth={1}
        />
        <MeterFill
          x={METER.x - METER.width / 2 + 1}
          y={METER.top + 1}
          width={METER.width - 2}
          height={meterHeight - 2}
          rx={(METER.width - 2) / 2}
          fill={PROBABILITY_PINK}
          style={{transform: `scaleY(${probability})`}}
        />
        <path
          d={`M ${METER.x - 6.5} ${halfY} H ${METER.x + 6.5}`}
          stroke="#78716C"
          strokeWidth={0.9}
          strokeDasharray="1.6 1.2"
        />
        <g key={`pill-${state.pages}-${state.drawn}`}>
          <Hop>
            <ProbabilityPill
              x={METER.x}
              y={METER.bottom + 1 - meterHeight * probability}
              text={formatProbability(probability)}
            />
          </Hop>
        </g>
      </svg>
    </ToyShell>
  );
}
